import type { PageGroup, PageGroupType } from '@st/sdk'
import { Button } from '@st/theme'
import { useProcess } from '~/store'
import { match } from '@st/pattern'
import { selectPageGroupEditor, type PageGroupEditorAction } from './page-group-editor'

const inputClass =
  'w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-blue-500'

export function DeliveryPageGroupMenu({
  group,
  groupTypes
}: {
  group: PageGroup
  groupTypes: PageGroupType[]
}) {
  const folder = useProcess('stFolder')
  const send = (action: PageGroupEditorAction) =>
    folder.send({
      type: 'taxReturnDelivery',
      message: { type: 'pageGroup', message: { type: 'pageGroupChanged', action } }
    })
  const view = selectPageGroupEditor(group, groupTypes)
  return (
    <div className="mt-3 grid gap-3 text-sm text-gray-700">
      <label className="grid gap-1">
        Type
        <select
          className={inputClass}
          value={view.selected}
          onChange={(event) => send({ type: 'typeSelected', value: event.target.value })}
        >
          <option value="">Leave blank</option>
          {view.unqualifiedCategory && (
            <option value={view.unqualifiedCategory.id} disabled>
              {view.unqualifiedCategory.name}
            </option>
          )}
          {view.options.map(({ id, name, choices }) => (
            <optgroup key={id} label={name}>
              {choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      {match(view.jurisdiction)
        .with({ type: 'none' }, () => null)
        .with({ type: 'state' }, ({ value }) => (
          <label className="grid gap-1">
            State
            <select
              className={inputClass}
              value={value}
              onChange={(event) => send({ type: 'stateSelected', code: event.target.value })}
            >
              <option value="" disabled>
                Select state
              </option>
              {view.states.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </label>
        ))
        .with({ type: 'local' }, ({ value }) => (
          <label className="grid gap-1">
            Local jurisdiction
            <input
              className={inputClass}
              value={value}
              placeholder="City or locality"
              onChange={(event) => send({ type: 'localityChanged', name: event.target.value })}
            />
          </label>
        ))
        .exhaustive()}
      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          onClick={() =>
            folder.send({
              type: 'taxReturnDelivery',
              message: { type: 'pageGroup', message: { type: 'pageGroupCancelled' } }
            })
          }
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!view.canSave}
          onClick={() => {
            folder.send({
              type: 'taxReturnDelivery',
              message: { type: 'pageGroup', message: { type: 'pageGroupSaveRequested' } }
            })
          }}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
